import { Camera, Check, CircleHelp, Loader, OctagonAlert, Printer, RefreshCw, ScanLine, Trash2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { Button, StatusMessage } from '@krp/ui-kit'

import '../../../shared/ui/onec.css'
import './inventory.css'
import { formatDateTime } from '../../pricing/model/format'
import { CommandStatus } from '../../../shared/ui/CommandStatus'
import { InputField } from '../../../shared/ui/InputField'
import { OneCQueryState } from '../../../shared/ui/OneCQueryState'
import { WarehouseSelect } from '../../structure/ui/WarehouseSelect'
import { metaOf } from '../../../shared/ui/meta'
import { useUnsavedGuard } from '../../../shared/ui/useUnsavedGuard'
import { BarcodeScanner } from '../../products/ui/scanner/BarcodeScanner'
import type { InventoryActions, InventoryVm, SaveBadge } from '../model/types'
import { CreateProductDialog } from './CreateProductDialog'
import { InventoryRowItem } from './InventoryRowItem'

/** Отметка сохранения: значок, слово и рамка — не только цвет (CLAUDE.md §7). */
const SAVE_BADGES: Record<Exclude<SaveBadge, 'idle'>, { icon: ReactNode; text: string }> = {
  saved: { icon: <Check size={14} aria-hidden="true" />, text: 'Сохранено на сервере' },
  pending: { icon: <Loader size={14} aria-hidden="true" />, text: 'Есть несохранённые правки…' },
  sending: { icon: <Loader size={14} aria-hidden="true" />, text: 'Сохранение…' },
  failed: { icon: <OctagonAlert size={14} aria-hidden="true" />, text: 'Не сохранено' },
  unknown: { icon: <CircleHelp size={14} aria-hidden="true" />, text: 'Исход сохранения неизвестен' },
}

/**
 * Инвентаризация склада одной базы 1С (карточка 100): обход полок сканером.
 * Сессия обхода живёт на сервере — после F5 и на другом рабочем месте она
 * продолжается; несохранённые правки уходят автосохранением. Склад
 * выбирается внутри ЭТОЙ базы. Экран получает vm и actions — ни store, ни HTTP.
 */
export function InventoryScreen({ vm, actions }: { vm: InventoryVm; actions: InventoryActions }) {
  const [barcode, setBarcode] = useState('')
  const [scanning, setScanning] = useState(false)
  const [creating, setCreating] = useState<{ rowId: string; barcode: string } | null>(null)
  useUnsavedGuard(vm.unsaved)

  const chosen = vm.warehouses.selected !== ''
  // Поле не гаснет на время запроса: иначе браузер снимает фокус и сканер-клавиатура пишет «в никуда».
  const scanDisabled = !vm.ready
  const submitScan = (code: string) => {
    if (code.trim() === '' || scanDisabled || vm.scan.busy) return
    actions.scanSubmitted(code)
    setBarcode('')
  }
  const badge = vm.save.badge === 'idle' ? null : SAVE_BADGES[vm.save.badge]

  return (
    <section className="onec-screen onec-inventory" aria-labelledby="onec-inventory-title">
      <header className="onec-toolbar">
        <h2 id="onec-inventory-title">Инвентаризация</h2>
        {badge !== null && (
          <span className="onec-inventory__save" data-badge={vm.save.badge} role={vm.save.badge === 'failed' || vm.save.badge === 'unknown' ? 'alert' : 'status'}>
            {badge.icon} {badge.text}
            {vm.save.badge === 'saved' && vm.save.savedAt !== null ? ` · ${formatDateTime(vm.save.savedAt)}` : ''}
          </span>
        )}
      </header>

      {(vm.save.badge === 'failed' || vm.save.badge === 'unknown') && (
        <div className="onec-command" data-status={vm.save.badge} role="alert">
          {vm.save.badge === 'unknown' ? <CircleHelp size={18} aria-hidden="true" /> : <OctagonAlert size={18} aria-hidden="true" />}
          <div className="onec-command__body">
            {vm.save.error !== null && <p className="onec-command__text">{vm.save.error.message}</p>}
            {vm.save.badge === 'unknown' && (
              <p className="onec-command__text">
                Ответ не получен: неизвестно, сохранила ли служба набор. Сохранение заменяет набор строк склада целиком —
                повтор строк не удвоит. Само по себе оно не повторяется: набор уйдёт снова по кнопке или со следующей
                вашей правкой.
              </p>
            )}
            {vm.save.error !== null && <p className="onec-command__meta">{metaOf(vm.save.error.code, vm.save.error.correlationId)}</p>}
            <div className="onec-command__actions">
              <Button variant="primary" onClick={actions.saveNow}>
                Сохранить ещё раз
              </Button>
            </div>
          </div>
        </div>
      )}

      <WarehouseSelect
        label="Склад этой базы"
        value={vm.warehouses.selected}
        pick={vm.warehouses.pick}
        placeholder="— выберите склад —"
        extra={vm.warehouses.current}
        disabled={vm.warehouses.lockedReason !== null}
        onChange={actions.warehouseChosen}
        onRetry={actions.reloadWarehouses}
      />
      {vm.warehouses.lockedReason !== null && chosen && <p className="onec-field__note">{vm.warehouses.lockedReason}</p>}
      {vm.warehouses.notice !== null && <StatusMessage kind="empty" title="Выбор склада снят" text={vm.warehouses.notice} />}

      {!chosen && vm.warehouses.query.status === 'ready' && (
        <p>Сначала выберите склад — цена и остаток зависят от него.</p>
      )}

      {chosen && (
        <>
          <OneCQueryState query={vm.session} emptyText="Сессии нет" onRetry={actions.reloadSession} />
          {vm.session.status === 'loading' && <p>Сканирование станет доступно после загрузки сохранённого обхода.</p>}

          <div className="onec-search" role="search">
            <InputField
              type="search"
              label="Штрихкод"
              value={barcode}
              onChange={setBarcode}
              onEnter={() => submitScan(barcode)}
              disabled={scanDisabled}
              autoFocus
            />
            <div className="onec-search__buttons">
              <Button
                variant="primary"
                icon={<ScanLine size={16} aria-hidden="true" />}
                busy={vm.scan.busy}
                disabled={scanDisabled || vm.scan.busy || barcode.trim() === ''}
                onClick={() => submitScan(barcode)}
              >
                Найти
              </Button>
              {vm.cameraScanAvailable && (
                <Button icon={<Camera size={16} aria-hidden="true" />} disabled={scanDisabled} onClick={() => setScanning(true)}>
                  Камера
                </Button>
              )}
              <Button
                variant="ghost"
                icon={<RefreshCw size={16} aria-hidden="true" />}
                busy={vm.session.refreshing}
                disabled={vm.unsaved}
                onClick={actions.reloadSession}
              >
                Перечитать
              </Button>
            </div>
          </div>
          {vm.scan.busy && <p aria-live="polite">Ищем товар в 1С…</p>}
          {vm.scan.error !== null && (
            <StatusMessage
              kind="error"
              title="Скан не выполнен"
              text={vm.scan.error.message}
              meta={metaOf(vm.scan.error.code, vm.scan.error.correlationId)}
            />
          )}

          {vm.ready && vm.rows.length === 0 && (
            <StatusMessage
              kind="empty"
              title="Сканируйте товары"
              text="Отсканированные позиции появятся здесь: цены складов, учётный остаток, отклонение."
            />
          )}

          {vm.rows.length > 0 && (
            <ul className="onec-inventory__rows" aria-label="Строки обхода">
              {vm.rows.map((row) => (
                <InventoryRowItem
                  key={row.id}
                  row={row}
                  disabled={!vm.ready}
                  onCount={(counted) => actions.countChanged(row.id, counted)}
                  onReprint={() => actions.reprintToggled(row.id)}
                  onRemove={() => actions.rowRemoved(row.id)}
                  onCreate={() => setCreating({ rowId: row.id, barcode: row.barcode })}
                  onResend={actions.operationResent}
                  onDismiss={actions.operationDismissed}
                />
              ))}
            </ul>
          )}

          {vm.clear !== null && vm.clearKey !== null && (
            <CommandStatus
              command={vm.clear}
              onResend={() => actions.operationResent(vm.clearKey ?? '')}
              onDismiss={() => actions.operationDismissed(vm.clearKey ?? '')}
            />
          )}

          {vm.rows.length > 0 && (
            <footer className="onec-inventory__totals">
              <span>
                Строк: <b>{vm.totals.rows}</b>
              </span>
              <span>
                Излишки: <b>{vm.totals.surplus}</b>
              </span>
              <span>
                Недостачи: <b>{vm.totals.shortage}</b>
              </span>
              <span>
                Ценников перепечатать: <b>{vm.totals.reprints}</b>
              </span>
              <div className="onec-actions">
                {/* Отметку «ценник не сходится» ставят ради печати: без кнопки товары
                    выписывали бы на бумажку и сканировали заново в ценниках. */}
                {vm.totals.reprints > 0 && (
                  <Button variant="primary" icon={<Printer size={16} aria-hidden="true" />} onClick={actions.printReprints}>
                    Печатать отмеченные ценники
                  </Button>
                )}
                <Button
                  variant="ghost"
                  icon={<Trash2 size={16} aria-hidden="true" />}
                  disabled={!vm.canClear}
                  onClick={actions.clearSession}
                >
                  Очистить сессию склада
                </Button>
              </div>
            </footer>
          )}
        </>
      )}

      {scanning && (
        <BarcodeScanner
          onClose={() => setScanning(false)}
          onCode={(code) => {
            setScanning(false)
            submitScan(code)
          }}
        />
      )}

      {creating !== null && (
        <CreateProductDialog
          rowId={creating.rowId}
          barcode={creating.barcode}
          warehouses={vm.createWarehouses}
          onClose={() => setCreating(null)}
          onCreate={(request) => {
            setCreating(null)
            actions.createProduct(request)
          }}
        />
      )}
    </section>
  )
}
